<template>
    <dashboard-layout title="근로계약 출근QR" fullHeight>
        <template slot="topBtn">
            <v-col cols="auto" class="pa-4">
                <attendance-code-form v-on="{ search }" @input="updateItem">
                    <template #activator="{ attrs, on }">
                        <v-btn color="primary" v-bind="attrs" v-on="on" class="min-w-100px min-w-lg-140px">생성하기</v-btn>
                    </template>
                </attendance-code-form>
            </v-col>
        </template>
        <template slot="contents">
            <template v-if="codes.length">
                <v-data-table
                    v-bind="{
                        items,
                        headers,
                        mobileBreakPoint: $vuetify.breakpoint.thresholds.sm,
                    }"
                    disable-filtering
                    disable-pagination
                    disable-sort
                    hide-default-footer
                    class="v-sheet--outlined rounded-0"
                >
                    <template v-for="header in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }">
                        <div :key="header.value">{{ header.formatter(value, item) }}</div>
                    </template>
                    <template #[`item.uses`]="{ item }">
                        <v-switch v-model="item.uses" v-bind="{ ...attrs_switch }" @change="(uses) => update({ _id: item._id, uses })" />
                    </template>
                    <template #[`item.view`]="{ item }">
                        <attendance-code-view
                            v-bind="{
                                value: item,
                            }"
                            @input="updateItem"
                        >
                            <template #activator="{ attrs, on }">
                                <v-btn small block color="primary" v-bind="attrs" v-on="on"> 확인하기 </v-btn>
                            </template>
                        </attendance-code-view>
                    </template>
                </v-data-table>
                <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-16" @input="(page) => $router.push({ query: { ...$route.query, page } })"></v-pagination>
            </template>

            <template v-else>
                <v-layout column justify-center align-center class="w-100 h-100 grey-f7 rounded-lg pa-20">
                    <p class="page-text--lg line-height-15 text-center mb-12 mb-lg-32">
                        안녕하세요, 담당자님. <br />
                        출근 QR코드가 없습니다.
                    </p>
                </v-layout>
            </template>

            <v-fade-transition>
                <v-overlay v-show="loading" color="white">
                    <v-progress-circular size="100" width="5" color="primary" indeterminate />
                </v-overlay>
            </v-fade-transition>
        </template>
    </dashboard-layout>
</template>

<script>
import api from "@/api";
import { attrs_switch } from "@/assets/variables";

import AttendanceCodeForm from "@/components/dashboard/company/attendance/code/attendance-code-form.vue";
import AttendanceCodeView from "@/components/dashboard/company/attendance/code/attendance-code-view.vue";
import DashboardLayout from "@/components/dashboard/dashboard-layout.vue";

const headers = [
    {
        text: "#",
        value: "index",
        width: 80,
        align: "end",
    },
    {
        text: "명칭",
        value: "name",
    },
    {
        text: "사용여부",
        value: "uses",
        align: "center",
        width: +96,
    },
    {
        text: "생성일",
        value: "createdAt",
        formatter: (value) => value?.toDateTime?.() || value || "-",
        width: 128,
    },
    {
        text: "QR",
        value: "view",
        width: 140,
    },
].map((item, index, array) => ({ ...item, divider: index < array.length - 1 }));

export default {
    components: {
        AttendanceCodeForm,
        AttendanceCodeView,
        DashboardLayout,
    },
    data: () => ({
        codes: [],
        summary: { totalCount: 0 },

        limit: 10,
        loading: false,

        headers,
        attrs_switch,
    }),
    computed: {
        items() {
            return this.codes.map((item, index) => ({ ...item, index: this.summary.totalCount - (this.page - 1) * this.limit - index }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            return { ...query };
        },
    },
    mounted() {
        this.init().then(this.search);
    },
    watch: {
        params() {
            this.init().then(this.search);
        },
        limit() {
            this.init().then(this.search);
        },
    },
    methods: {
        async init() {
            this.codes = [];
            this.summary = { totalCount: 0 };
            this.selected = [];
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;
            try {
                let { skip, limit, params } = this;
                const { summary, codes } = await api.v1.company.attendance.codes.gets({
                    headers: { skip, limit },
                    params,
                });
                this.summary = summary;
                this.codes = codes;
            } finally {
                this.loading = false;
            }
        },

        async remove(item) {
            if (!confirm("삭제하시겠습니까?")) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                await api.v1.company.attendance.codes.delete({ _id: item._id });

                alert("삭제되었습니다.");
            } finally {
                this.loading = false;
                this.search();
            }
        },

        updateItem(item) {
            let index = this.codes.findIndex(({ _id }) => _id == item._id);
            if (-1 < index) this.codes.splice(index, 1, item);
            else this.codes = [item, ...this.codes];
            this.search();
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    th,
    td {
        &:first-child {
            border-right: 1px solid var(--border-color);

            .v-input--selection-controls__input {
                margin: 0;

                i.v-icon {
                    &.mdi-checkbox-marked,
                    &.mdi-minus-box {
                        color: var(--v-primary-base) !important;
                    }
                }
            }
        }
    }
}
</style>
